import invoicesService from '@/shared/services/invoices.service'
import i18n from '@/app/i18n'

import { TOAST_DISPATCHERS } from '@/shared/constants/store-constants/toast'
import { INVOICES_DISPATCHERS } from '@/shared/constants/store-constants/invoices'
import { checkNextPage } from '@/shared/lib/utils/pagination'

const ALL_INVOICE_STATUSES = [
  'waiting_for_deposit',
  'partially_paid',
  'payment_accepted',
  'finished',
  'cancelled',
  'expired',
]

/**
 * Invoice actions module
 *
 * Success notifications for invoice creation/duplication are now handled via URL query parameters
 * in the UI layer instead of being dispatched from store actions. This allows for better control
 * over when and how notifications are displayed after route navigation.
 */

export default {
  onSearch({ commit, dispatch }, payload) {
    commit('SET_SEARCH', payload)
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
  },

  resetSearch({ commit, dispatch }) {
    commit('RESET_SEARCH')
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
  },

  setStatusFilter({ commit, dispatch }, payload) {
    commit('SET_STATUS_FILTER', payload)
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
  },

  setDateFromFilter({ commit, dispatch }, payload) {
    commit('SET_DATE_FROM_FILTER', payload)
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
  },

  setDateToFilter({ commit, dispatch }, payload) {
    commit('SET_DATE_TO_FILTER', payload)
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
  },

  setIsOneTimeFilter({ commit, dispatch }, payload) {
    commit('SET_IS_ONE_TIME_FILTER', payload)
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
  },

  setIsUnfixedAmountFilter({ commit, dispatch }, payload) {
    commit('SET_IS_UNFIXED_AMOUNT_FILTER', payload)
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
  },

  resetFilters({ commit, dispatch }) {
    commit('RESET_FILTERS')
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
  },

  setDateRange({ commit, dispatch }, payload) {
    if (payload && payload.length === 2) {
      commit('SET_DATE_FROM_FILTER', payload[0])
      commit('SET_DATE_TO_FILTER', payload[1])
    } else {
      commit('SET_DATE_FROM_FILTER', null)
      commit('SET_DATE_TO_FILTER', null)
    }
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
  },

  resetStore({ commit }, payload) {
    commit('RESET_STORE', payload)
  },

  changePage({ commit, dispatch, state }) {
    if (state.loading) return
    if (checkNextPage(state.page, state.total, state.limit)) {
      commit('SET_PAGE', state.page + 1)
      dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
    }
  },

  onSelectSort({ commit, dispatch }, payload) {
    commit('SET_SORT', payload)
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(INVOICES_DISPATCHERS.getInvoices, {}, { root: true })
  },

  onSelectChronologySort({ commit, dispatch, state }, payload) {
    commit('SET_CHRONOLOGY_SORT', payload)
    if (state.currentInvoice?.id) {
      dispatch('getInvoiceDetails', state.currentInvoice.id)
    }
  },

  async createInvoice({ commit, dispatch }, { formData }) {
    try {
      commit('SET_ACTION_LOADING', true)
      const response = await invoicesService.createInvoice(
        formData || arguments[1]
      )

      // Success notification is handled in the UI via query parameters for better UX control
      return { success: true, payload: response }
    } catch (error) {
      if (!error.isHandled) {
        const serverMessage =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          null

        dispatch(
          TOAST_DISPATCHERS.setErrorMessage,
          {
            title: i18n.global.t('ToastError | title'),
            message: serverMessage || i18n.global.t('ToastError | message'),
          },
          { root: true }
        )
      }
      return { success: false, error }
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },

  async getInvoices({ commit, state, dispatch }, params) {
    const version = state._requestVersion

    try {
      commit('SET_LOADING', true)

      const requestParams = {
        ...params,
        page: state.page,
        limit: state.limit,
        sort_by: state.sort.sort_by,
        sort_direction: state.sort.sort_direction,
      }

      if (state.search) {
        requestParams.search = state.search
      }

      requestParams.statuses =
        state.statusFilter && state.statusFilter.length > 0
          ? state.statusFilter
          : ALL_INVOICE_STATUSES

      if (state.dateFromFilter) {
        requestParams.created_at_from = state.dateFromFilter
      }

      if (state.dateToFilter) {
        requestParams.created_at_to = state.dateToFilter
      }

      if (state.isOneTimeFilter !== null) {
        requestParams.is_one_time = state.isOneTimeFilter
      }

      if (state.isUnfixedAmountFilter !== null) {
        requestParams.is_unfixed_amount = state.isUnfixedAmountFilter
      }

      const response = await invoicesService.getInvoices(requestParams)
      commit('SET_ITEMS', { payload: response.data, version })
    } catch (error) {
      if (!error.isHandled) {
        dispatch(
          TOAST_DISPATCHERS.setErrorMessage,
          {
            title: i18n.global.t('ToastError | title'),
            message: i18n.global.t('ToastError | message'),
          },
          { root: true }
        )
      }
    } finally {
      if (version === state._requestVersion) {
        commit('SET_LOADING', false)
      }
    }
  },

  async getInvoiceById({ commit, dispatch }, invoiceId) {
    try {
      commit('SET_ACTION_LOADING', true)
      const response = await invoicesService.getInvoiceById(invoiceId)

      return { success: true, payload: response.data }
    } catch (error) {
      dispatch(
        TOAST_DISPATCHERS.setErrorMessage,
        {
          title: i18n.global.t('ToastError | title'),
          message: i18n.global.t('ToastError | message'),
        },
        { root: true }
      )
      return { success: false, error }
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },

  async cancelInvoice({ commit, dispatch }, invoiceId) {
    try {
      commit('SET_ACTION_LOADING', true)
      await invoicesService.cancelInvoice(invoiceId)

      dispatch(
        TOAST_DISPATCHERS.setSuccessMessage,
        {
          title: i18n.global.t('Invoices | actions | cancel | success | title'),
          message: i18n.global.t(
            'Invoices | actions | cancel | success | message'
          ),
        },
        { root: true }
      )

      return { success: true }
    } catch (error) {
      dispatch(
        TOAST_DISPATCHERS.setErrorMessage,
        {
          title: i18n.global.t('ToastError | title'),
          message: i18n.global.t('ToastError | message'),
        },
        { root: true }
      )
      return { success: false, error }
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },

  async resendInvoice({ commit, dispatch }, invoiceId) {
    try {
      commit('SET_ACTION_LOADING', true)
      await invoicesService.resendInvoice(invoiceId)

      dispatch(
        TOAST_DISPATCHERS.setSuccessMessage,
        {
          title: i18n.global.t('Invoices | actions | resend | success | title'),
          message: i18n.global.t(
            'Invoices | actions | resend | success | message'
          ),
        },
        { root: true }
      )

      return { success: true }
    } catch (error) {
      dispatch(
        TOAST_DISPATCHERS.setErrorMessage,
        {
          title: i18n.global.t('ToastError | title'),
          message: i18n.global.t('ToastError | message'),
        },
        { root: true }
      )
      return { success: false, error }
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },

  async getInvoiceDetails({ commit, dispatch, state }, invoiceId) {
    try {
      commit('SET_ACTION_LOADING', true)
      const response = await invoicesService.getInvoiceById(invoiceId, {
        chronology_sort_by: state.chronologySort.sort_by,
        chronology_sort_direction: state.chronologySort.sort_direction,
      })

      commit('SET_CURRENT_INVOICE', response.data)

      return { success: true, payload: response.data }
    } catch (error) {
      dispatch(
        TOAST_DISPATCHERS.setErrorMessage,
        {
          title: i18n.global.t('ToastError | title'),
          message: i18n.global.t('ToastError | message'),
        },
        { root: true }
      )
      return { success: false, error }
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },

  async getInvoiceDetailsPolling({ commit, state }, invoiceId) {
    try {
      commit('SET_POLLING_LOADING', true)
      const response = await invoicesService.getInvoiceById(invoiceId, {
        chronology_sort_by: state.chronologySort.sort_by,
        chronology_sort_direction: state.chronologySort.sort_direction,
      })

      commit('SET_CURRENT_INVOICE', response.data)

      return { success: true, payload: response.data }
    } catch (error) {
      console.error('Polling error:', error)
      return { success: false, error }
    } finally {
      commit('SET_POLLING_LOADING', false)
    }
  },

  resetInvoiceDetails({ commit }) {
    commit('RESET_CURRENT_INVOICE')
  },

  async getInvoiceLink({ commit, dispatch }, { invoiceId, language = 'ru' }) {
    try {
      commit('SET_ACTION_LOADING', true)
      const response = await invoicesService.getInvoiceLink(invoiceId, language)
      return { success: true, payload: response.data }
    } catch (error) {
      dispatch(
        TOAST_DISPATCHERS.setErrorMessage,
        {
          title: i18n.global.t('ToastError | title'),
          message: i18n.global.t('ToastError | message'),
        },
        { root: true }
      )
      return { success: false, error }
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },

  async duplicateInvoice({ dispatch }, { invoiceData }) {
    const formData = {
      data: {
        amount: parseFloat(
          invoiceData.payment_amount || invoiceData.required_payment_amount
        ),
        buyer_email: invoiceData.buyer_email,
        buyer_name: invoiceData.buyer_name,
        coin_id: invoiceData.coin_id || 'usdt',
        description: invoiceData.description || invoiceData.title,
        network_id: 'trc20',
        title: invoiceData.description || invoiceData.title,
      },
      external_id: `invoice_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      invoice_duration: 7 * 24 * 60 * 60 * 1000000000, // 7 дней по умолчанию
    }
    const result = await dispatch('createInvoice', {
      formData,
      showNotification: false,
    })

    return result
  },
}
